/**
 * DmeCard v237.4.0 — patient Equipment & Supplies (DME) card
 *
 * DmeItemCard: each ordered item with an Equipment chip, quantity, HCPCS, a
 * "Learn how to use it" primer (what it is / how to use it / caring for it,
 * Haiku-generated & cached), and a "Mark as reviewed" completion.
 *
 * DmeSupplierSection: "Where to get it" — shows the MD-recommended supplier when
 * the DME order carries one, plus a patient supplier search (Google Places),
 * saved to deliver.dmeFacility.
 *
 * Self-contained; no shared-component edits. Reduced palette: navy/slate
 * identity, blue only for Learn more, green only for in-network / reviewed.
 */
import { useState, useEffect, useCallback } from 'react'
import { Check, ChevronDown, MapPin, Search, ShieldCheck } from 'lucide-react'
import { asText, asFirst } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const GREEN = '#66D170'

const Package = ({ size = 20, style = {} }) => (
  <svg style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16.5 9.4 7.5 4.2" /><path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.3 7 12 12 20.7 7" /><line x1="12" y1="22" x2="12" y2="12" />
  </svg>
)

// ★ PROD-1156: each field drawn from an order is text (utils/asText.js), never an object.
const dmeName = (item) => typeof item === 'string' ? item : (asFirst(item?.device, item?.name, item?.text, item?.description) || 'Equipment')
const dmeHcpcs = (item) => item && typeof item === 'object' ? asFirst(item.hcpcsCode, item.hcpcs, item.code) : ''
const dmeQty = (item) => item && typeof item === 'object' ? asFirst(item.quantity, item.qty) : ''

export function DmeItemCard({ item, edu, reviewed, isOpen, onToggleReview, onToggleOpen, ts }) {
  const hasEdu = !!(edu && (edu.whatItIs || edu.howToUse || edu.caring))
  const hcpcs = dmeHcpcs(item)
  const qty = dmeQty(item)
  return (
    <div className={`w-full rounded-[14px] overflow-hidden border-2 mb-2.5 transition-all ${reviewed ? 'border-green-200' : 'border-gray-200'} bg-white`}>
      <div className="h-[3px]" style={{ backgroundColor: reviewed ? GREEN : NAVY }} />
      <div className="p-3.5 flex items-start gap-3">
        <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: '#F1F5F9' }}>
          <Package size={19} style={{ color: NAVY }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] font-bold leading-snug" style={{ color: NAVY }}>{dmeName(item)}</p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: '#EEF2F6', color: '#475569' }}>{ts('instructions.equipment', 'Equipment')}</span>
            {qty && <span className="text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded" style={{ background: '#EEF2F6', color: '#475569' }}>{ts('instructions.qty', 'Qty')} {qty}</span>}
            {hcpcs && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: '#EEF2F6', color: '#475569' }}>HCPCS {hcpcs}</span>}
          </div>
          {hasEdu && (
            <button onClick={onToggleOpen} className="inline-flex items-center gap-1.5 mt-2 bg-transparent p-0 cursor-pointer"
              style={{ color: BLUE, fontSize: 12.5, fontWeight: 700, border: 'none' }}>
              {ts('instructions.learnHowToUse', 'Learn how to use it')}
              <ChevronDown size={14} strokeWidth={2.5} style={{ transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
            </button>
          )}
        </div>
      </div>

      {hasEdu && (
        <div className="overflow-hidden" style={{ transition: 'max-height .3s ease', maxHeight: isOpen ? 460 : 0 }}>
          <div className="mx-3.5 rounded-[11px] px-3 py-2.5" style={{ background: '#F6FAFD', border: '1px solid #E4EFF7' }}>
            {edu.whatItIs && (<>
              <p className="text-[9.5px] font-extrabold uppercase tracking-wide mb-0.5" style={{ color: BLUE }}>{ts('instructions.whatThisIs', 'What this is')}</p>
              <p className="text-[12px] leading-relaxed font-medium mb-2" style={{ color: '#475569' }}>{edu.whatItIs}</p>
            </>)}
            {edu.howToUse && (<>
              <p className="text-[9.5px] font-extrabold uppercase tracking-wide mb-0.5" style={{ color: BLUE }}>{ts('instructions.howToUseIt', 'How to use it')}</p>
              <p className="text-[12px] leading-relaxed font-medium mb-2" style={{ color: '#475569' }}>{edu.howToUse}</p>
            </>)}
            {edu.caring && (<>
              <p className="text-[9.5px] font-extrabold uppercase tracking-wide mb-0.5" style={{ color: BLUE }}>{ts('instructions.caringForIt', 'Caring for it')}</p>
              <p className="text-[12px] leading-relaxed font-medium" style={{ color: '#475569' }}>{edu.caring}</p>
            </>)}
          </div>
        </div>
      )}

      <div className="mx-3.5 my-3 pt-3 border-t border-gray-100 flex items-center gap-3 cursor-pointer" onClick={onToggleReview}>
        <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
          style={reviewed ? { backgroundColor: GREEN, border: 'none' } : { background: 'transparent', border: '2px solid #CBD5E1' }}>
          {reviewed && <Check size={14} className="text-white" strokeWidth={2.5} />}
        </span>
        <span className="text-[12.5px] font-bold" style={{ color: NAVY }}>
          {reviewed ? ts('instructions.reviewedLabel', 'Reviewed') : ts('instructions.markReviewed', 'Mark as reviewed')}
        </span>
      </div>
    </div>
  )
}

export function DmeSupplierSection({ recommended, currentCase, updateDeliverTask, ts }) {
  const saved = currentCase?.deliver?.dmeFacility || null
  const [selected, setSelected] = useState(saved)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => { if (saved && !selected) setSelected(saved) }, [saved])

  const search = useCallback((q) => {
    if (!q || q.length < 3) { setResults([]); return }
    setLoading(true)
    if (window.google?.maps?.places) {
      const svc = new window.google.maps.places.PlacesService(document.createElement('div'))
      svc.textSearch({ query: `${q} medical supply durable medical equipment`, type: 'health' }, (res, status) => {
        if (status === 'OK' && res) {
          setResults(res.slice(0, 5).map(p => ({ name: p.name, address: p.formatted_address, placeId: p.place_id })))
        }
        setLoading(false)
      })
    } else { setLoading(false) }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => { if (query.length >= 3) search(query) }, 400)
    return () => clearTimeout(t)
  }, [query, search])

  const pick = (f) => {
    setSelected(f); setQuery(''); setResults([])
    try { updateDeliverTask?.('dme', { dmeFacility: f }) } catch (e) { /* non-fatal */ }
  }

  return (
    <div className="mx-4 mb-4 rounded-[14px] border-[1.5px] bg-white overflow-hidden" style={{ borderColor: '#A7D9FC', borderTopWidth: 3, borderTopColor: NAVY }}>
      <div className="p-3.5">
        <div className="flex items-center gap-2 mb-2.5">
          <MapPin size={15} style={{ color: NAVY }} />
          <span className="text-[13px] font-bold" style={{ color: NAVY }}>{ts('instructions.whereToGetIt', 'Where to get it')}</span>
        </div>

        {recommended?.name && (!selected || selected.name === recommended.name) && (
          <div className="flex items-start gap-2 rounded-[10px] px-2.5 py-2 mb-2.5" style={{ background: '#F8FAFC', border: '1px solid #EDF1F5' }}>
            <ShieldCheck size={14} style={{ color: GREEN, flexShrink: 0, marginTop: 1 }} />
            <div>
              <p className="text-[12.5px] font-bold" style={{ color: NAVY }}>{recommended.name}</p>
              {recommended.address && <p className="text-[10.5px] font-medium" style={{ color: '#64748B' }}>{recommended.address}</p>}
              <p className="text-[10.5px] font-semibold" style={{ color: '#16794C' }}>{ts('instructions.recommendedSupplier', 'Recommended by your doctor')}</p>
            </div>
          </div>
        )}

        {selected && selected.name !== recommended?.name && (
          <div className="flex items-start gap-2 rounded-[10px] px-2.5 py-2 mb-2.5" style={{ background: '#F8FAFC', border: '1px solid #EDF1F5' }}>
            <MapPin size={14} style={{ color: '#94A3B8', flexShrink: 0, marginTop: 1 }} />
            <div>
              <p className="text-[12.5px] font-bold" style={{ color: NAVY }}>{selected.name}</p>
              {selected.address && <p className="text-[10.5px] font-medium" style={{ color: '#64748B' }}>{selected.address}</p>}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 rounded-[10px] px-3 py-2" style={{ border: '1px solid #E2E8F0' }}>
          <Search size={14} style={{ color: '#94A3B8' }} />
          <input value={query} onChange={e => setQuery(e.target.value)}
            placeholder={ts('instructions.searchSupplier', 'Search name, address, or zip')}
            className="flex-1 text-[12.5px] font-medium bg-transparent outline-none" style={{ color: NAVY }} />
          {loading && <OrellaSpinner size={16} />}
        </div>

        {results.length > 0 && (
          <div className="mt-2 rounded-[10px] overflow-hidden" style={{ border: '1px solid #EDF1F5' }}>
            {results.map((f, i) => (
              <button key={i} onClick={() => pick(f)} className="w-full text-left px-3 py-2 bg-white hover:bg-slate-50"
                style={{ borderTop: i ? '1px solid #F1F5F9' : 'none' }}>
                <p className="text-[12.5px] font-semibold" style={{ color: NAVY }}>{f.name}</p>
                {f.address && <p className="text-[10.5px]" style={{ color: '#94A3B8' }}>{f.address}</p>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
